import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@egaranti/components";
import { Button } from "@egaranti/components";
import { Textarea } from "@egaranti/components";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, XCircle, FileText, ExternalLink } from "lucide-react";

const REJECTION_REASONS = [
  "Fatura tarihi hatalı",
  "Eksik veya geçersiz belge",
  "Ürün kampanya kapsamı dışında",
  "Müşteri bilgileri hatalı",
];

const ApplicationDetailModal = ({
  isOpen,
  onClose,
  application,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("detail"); // "detail" | "reject"
  const [selectedReason, setSelectedReason] = useState(null);
  const [customReason, setCustomReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  if (!application) return null;

  const resetRejectState = () => {
    setStep("detail");
    setSelectedReason(null);
    setCustomReason("");
    setRejecting(false);
  };

  const handleClose = () => {
    resetRejectState();
    onClose();
  };

  const handleRejectClick = () => {
    setStep("reject");
  };

  const handleBackToDetail = () => {
    resetRejectState();
  };

  const handleConfirmReject = async () => {
    const reason =
      selectedReason === "OTHER" ? customReason.trim() : selectedReason;

    if (!reason) return;

    setRejecting(true);
    try {
      await onReject(application, reason);
      resetRejectState();
    } catch {
      setRejecting(false);
    }
  };

  const isRejectDisabled =
    rejecting ||
    !selectedReason ||
    (selectedReason === "OTHER" && !customReason.trim());

  const getStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            {t("applicationsPage.status.approved", "Onaylandı")}
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            {t("applicationsPage.status.rejected", "Reddedildi")}
          </span>
        );
      case "PROCESSED":
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {t("applicationsPage.status.processed", "İşlendi")}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
            {t("applicationsPage.status.pending", "Beklemede")}
          </span>
        );
    }
  };

  // ── REJECT STEP ──
  if (step === "reject") {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-white sm:max-w-[480px]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-base">
                  {t("applicationsPage.rejectReason", "Red Nedeni")}
                </DialogTitle>
                <DialogDescription className="mt-0.5">
                  {application.customerFirstName} {application.customerLastName}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-2 py-3">
            {REJECTION_REASONS.map((reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => {
                  setSelectedReason(reason);
                  setCustomReason("");
                }}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                  selectedReason === reason
                    ? "border-red-300 bg-red-50 text-red-900 ring-1 ring-red-200"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    selectedReason === reason
                      ? "border-red-500 bg-red-500"
                      : "border-gray-300"
                  }`}
                >
                  {selectedReason === reason && (
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  )}
                </div>
                <span className="font-medium">{reason}</span>
              </button>
            ))}

            {/* Diğer seçeneği */}
            <button
              type="button"
              onClick={() => setSelectedReason("OTHER")}
              className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                selectedReason === "OTHER"
                  ? "border-red-300 bg-red-50 text-red-900 ring-1 ring-red-200"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  selectedReason === "OTHER"
                    ? "border-red-500 bg-red-500"
                    : "border-gray-300"
                }`}
              >
                {selectedReason === "OTHER" && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </div>
              <span className="font-medium">
                {t("applicationsPage.otherReason", "Diğer")}
              </span>
            </button>

            {selectedReason === "OTHER" && (
              <div className="pl-7 pt-1">
                <Textarea
                  placeholder={t(
                    "applicationsPage.writeRejectReason",
                    "Red nedenini yazınız...",
                  )}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={3}
                  className="resize-none text-sm"
                  autoFocus
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondaryGray" onClick={handleBackToDetail}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t("common.back", "Geri")}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmReject}
              disabled={isRejectDisabled}
            >
              {rejecting
                ? t("common.processing", "İşleniyor...")
                : t("common.reject", "Reddet")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── DETAIL STEP ──
  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {application.applicantStatus === "APPROVED" && (
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            )}
            {application.applicantStatus === "REJECTED" && (
              <XCircle className="h-6 w-6 text-red-500" />
            )}
            {application.applicantStatus === "PENDING" && (
              <Clock className="h-6 w-6 text-yellow-500" />
            )}
            {application.applicantStatus === "PROCESSED" && (
              <CheckCircle2 className="h-6 w-6 text-blue-500" />
            )}
            <DialogTitle>{t("applicationsPage.applicationDetail")}</DialogTitle>
          </div>
          <DialogDescription>
            {application.customerFirstName} {application.customerLastName} • {application.merchantProductName || "-"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-md border border-gray-100 bg-gray-50/50 p-3">
            <div>
              <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                {t("applicationsPage.customer")}
              </h4>
              <p className="text-sm font-semibold text-gray-900">
                {application.customerFirstName} {application.customerLastName}
              </p>
              <p className="text-xs text-gray-500">
                {application.customerEmail}
              </p>
              <p className="text-xs text-gray-500">
                {application.customerPhone}
              </p>
              {application.customerIdentificationNumber && (
                <p className="text-xs text-gray-500">
                  TC: {application.customerIdentificationNumber}
                </p>
              )}
            </div>
            <div>
              <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                {t("applicationsPage.product")}
              </h4>
              <p className="text-sm font-semibold text-gray-900">
                {application.merchantProductName || "-"}
              </p>
              {application.productInvoiceDate && (
                <p className="text-xs text-gray-500">
                  {t("applicationsPage.invoiceDate")}: {application.productInvoiceDate}
                </p>
              )}
              {application.serialNumber && (
                <p className="text-xs text-gray-500">
                  {t("applicationsPage.serialNumber", "Seri No")}: {application.serialNumber}
                </p>
              )}
              {application.billingDocumentUrl ? (
                <a
                  href={application.billingDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300"
                >
                  <FileText className="h-4 w-4" />
                  {t("applicationsPage.viewInvoice", "Fatura Belgesi")}
                  <ExternalLink className="h-3.5 w-3.5 text-blue-400" />
                </a>
              ) : (
                <p className="mt-1 text-xs text-gray-400 italic">
                  {t("applicationsPage.noInvoice", "Fatura yüklenmemiş")}
                </p>
              )}
            </div>

            <div>
              <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                {t("applicationsPage.campaign")}
              </h4>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                {application.campaignName}
              </span>
            </div>
            <div>
              <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                {t("applicationsPage.status")}
              </h4>
              {getStatusBadge(application.applicantStatus)}
            </div>

            <div>
              <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                {t("applicationsPage.applicationDate")}
              </h4>
              <p className="text-sm text-gray-900">
                {application.createdAt || "-"}
              </p>
            </div>
            {application.reviewedAt && (
              <div>
                <h4 className="mb-0.5 text-xs font-medium text-gray-400">
                  {t("applicationsPage.reviewedAt")}
                </h4>
                <p className="text-sm text-gray-900">
                  {application.reviewedAt}
                </p>
              </div>
            )}
          </div>

          {application.customerComment && (
            <div className="rounded-md bg-gray-50 px-3 py-2">
              <h4 className="mb-0.5 text-xs font-medium text-gray-500">
                {t("applicationsPage.customerComment")}
              </h4>
              <p className="text-sm text-gray-600">
                {application.customerComment}
              </p>
            </div>
          )}

          {application.reviewComment && (
            <div className="rounded-md bg-blue-50 px-3 py-2">
              <h4 className="mb-0.5 text-xs font-medium text-blue-700">
                {t("applicationsPage.reviewComment")}
              </h4>
              <p className="text-sm text-blue-600">
                {application.reviewComment}
              </p>
            </div>
          )}

          {application.fieldValues && application.fieldValues.length > 0 && (
            <div className="rounded-md border p-3">
              <h4 className="mb-1.5 text-xs font-medium text-gray-500">
                {t("applicationsPage.formFieldValues", "Form Alanları")}
              </h4>
              <div className="space-y-1.5">
                {application.fieldValues.map((fv) => (
                  <div key={fv.id} className="flex justify-between border-b border-gray-100 pb-1 last:border-0">
                    <span className="text-xs text-gray-500">{fv.question}</span>
                    <span className="text-sm font-medium text-gray-900">{fv.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {application.applicantStatus === "PENDING" && (
            <div className="rounded-md bg-gray-50 px-3 py-2.5">
              <p className="text-sm text-gray-600">
                {t("applicationsPage.approvalConfirmation")}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondaryGray" onClick={handleClose}>
            {t("common.close")}
          </Button>
          {application.applicantStatus === "PENDING" && (
            <>
              <button
                className="font-medium text-red-500 hover:text-red-600 hover:underline"
                onClick={handleRejectClick}
              >
                {t("common.reject")}
              </button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onApprove(application)}
              >
                {t("common.approve")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    </>
  );
};

export default ApplicationDetailModal;
